import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { websiteSupabase } from "@/integrations/supabase/website";
import { resolveAllowedDomains } from "../../scripts/allowlist.mjs";

// Cloudflare Access is the hard edge gate on admin.firstier.ai (canonical, #314) / admin.junus.ai + admin.qadar.ai (until their 301s). This
// in-app check is defence-in-depth: only @<allowed-domain> staff (plus one explicit
// address) count as authorized once a Supabase session exists.
// VITE_ADMIN_ALLOWED_DOMAIN may be a single domain or a comma/space separated list;
// the junus.ai move (#266) admits BOTH qadar.ai + junus.ai during the parallel-hosts
// window.
// An empty/blank value (unset CI variable → "") falls back to the default list (#300).
const ALLOWED_DOMAINS = resolveAllowedDomains(import.meta.env.VITE_ADMIN_ALLOWED_DOMAIN);
const ALLOWED_EMAILS = ["ds@beryl-holding.de"];

function isAllowedEmail(email: string | undefined | null): boolean {
  if (!email) return false;
  const normalized = email.toLowerCase();
  if (ALLOWED_EMAILS.includes(normalized)) return true;
  return ALLOWED_DOMAINS.some((d) => normalized.endsWith(`@${d}`));
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** True when a session exists AND the email is allowed into the console. */
  isAuthorized: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: sub } = websiteSupabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s);
      Sentry.setUser(s?.user ? { id: s.user.id, email: s.user.email } : null);
    });

    websiteSupabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) Sentry.setUser({ id: s.user.id, email: s.user.email });
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const user = session?.user ?? null;
    return {
      session,
      user,
      loading,
      isAuthorized: !!session && isAllowedEmail(user?.email),
      signOut: async () => {
        await websiteSupabase.auth.signOut();
      },
    };
  }, [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located with the provider on purpose
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
